import { create } from 'zustand';
import type { Organization, OrganizationMember, OrganizationInvitation, Client } from '../types';
import { organizationsApi } from '../services/api/organizations';
import { clientsApi } from '../services/api/clients';

/**
 * useOrganizationStore — plan 3 task A2 (#778).
 *
 * Houdt detail-state voor de op dit moment actieve organisatie bij. Wordt
 * lazy geladen op basis van `useSessionStore.organizationId`. Componenten
 * mogen rechtstreeks `currentOrg` lezen; mutaties (assignLicense etc.) gaan
 * via deze store zodat ze automatisch een refresh triggeren.
 */
interface OrganizationState {
  currentOrg: Organization | null;
  members: OrganizationMember[];
  pendingInvitations: OrganizationInvitation[];
  /** Clients (opdrachtgevers) van de actieve organisatie (#843 Task 1.6). */
  clients: Client[];
  loading: boolean;
  error: string | null;

  loadOrg: (orgId: string) => Promise<void>;
  refresh: () => Promise<void>;
  clear: () => void;

  // Clients (#843 Task 1.6)
  loadClients: (orgId: string) => Promise<void>;
  addClient: (client: Client) => void;
  updateClient: (client: Client) => void;
  removeClient: (id: string) => void;

  // Mutaties
  assignMemberLicense: (accountId: string, billingPeriod: 'annual' | 'monthly') => Promise<void>;
  revokeMemberLicense: (accountId: string) => Promise<void>;
  changeMemberLicensePeriod: (
    accountId: string,
    billingPeriod: 'annual' | 'monthly'
  ) => Promise<void>;
  resizePool: (annualSize: number) => Promise<void>;
  inviteMember: (
    email: string,
    role: 'admin' | 'member',
    billingPeriod: 'annual' | 'monthly',
    message?: string
  ) => Promise<void>;
  removeMember: (accountId: string) => Promise<void>;
  changeMemberRole: (accountId: string, newRole: 'admin' | 'member') => Promise<void>;

  /**
   * Phase 3 (#842): optimistisch verwijderen van een pending invitation uit de
   * lokale state na een succesvolle revoke. Voorkomt een full refresh-trip
   * voor wat een puur cosmetische update is.
   */
  removeInvitation: (invitationId: string) => void;
}

export const useOrganizationStore = create<OrganizationState>((set, get) => ({
  currentOrg: null,
  members: [],
  pendingInvitations: [],
  clients: [],
  loading: false,
  error: null,

  loadOrg: async (orgId) => {
    set({ loading: true, error: null });
    try {
      const [org, members] = await Promise.all([
        organizationsApi.getOrganization(orgId),
        organizationsApi.getMembers(orgId),
      ]);
      // Uitnodigingen zijn owner/admin-only. Eerder vroeg elk lid ze toch op
      // en slikte de 403 — maar de 403 had dan al de toast "Je hebt geen
      // bewerkrechten voor dit project" afgevuurd, bij elke paginaload (NC #651).
      const viewerRole = org.role ?? org.memberRole;
      const invitations =
        viewerRole === 'owner' || viewerRole === 'admin'
          ? await organizationsApi
              .getOrgPendingInvitations(orgId)
              .catch(() => [] as OrganizationInvitation[])
          : ([] as OrganizationInvitation[]);
      set({
        currentOrg: org,
        members,
        pendingInvitations: invitations,
        loading: false,
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to load organization',
        loading: false,
      });
    }
  },

  refresh: async () => {
    const orgId = get().currentOrg?.id;
    if (orgId) await get().loadOrg(orgId);
  },

  clear: () =>
    set({
      currentOrg: null,
      members: [],
      pendingInvitations: [],
      clients: [],
      error: null,
    }),

  // Clients (#843 Task 1.6) — basale CRUD-helpers; create/update via clientsApi
  // worden in latere fases lokaal gespiegeld via add/update/removeClient om
  // round-trips te besparen.
  loadClients: async (orgId: string) => {
    try {
      const clients = await clientsApi.list(orgId);
      set({ clients });
    } catch (err) {
      console.warn('Failed to load clients:', err);
    }
  },
  addClient: (client: Client) => set((s) => ({ clients: [...s.clients, client] })),
  updateClient: (client: Client) =>
    set((s) => ({
      clients: s.clients.map((c) => (c.id === client.id ? client : c)),
    })),
  removeClient: (id: string) => set((s) => ({ clients: s.clients.filter((c) => c.id !== id) })),

  assignMemberLicense: async (accountId, billingPeriod) => {
    const orgId = get().currentOrg?.id;
    if (!orgId) return;
    await organizationsApi.assignLicense(orgId, accountId, billingPeriod);
    await get().refresh();
  },

  revokeMemberLicense: async (accountId) => {
    const orgId = get().currentOrg?.id;
    if (!orgId) return;
    await organizationsApi.revokeLicense(orgId, accountId);
    await get().refresh();
  },

  changeMemberLicensePeriod: async (accountId, billingPeriod) => {
    const orgId = get().currentOrg?.id;
    if (!orgId) return;
    await organizationsApi.changeLicensePeriod(orgId, accountId, billingPeriod);
    await get().refresh();
  },

  resizePool: async (annualSize) => {
    const orgId = get().currentOrg?.id;
    if (!orgId) return;
    await organizationsApi.setPoolSize(orgId, annualSize);
    await get().refresh();
  },

  inviteMember: async (email, role, billingPeriod, message) => {
    const orgId = get().currentOrg?.id;
    if (!orgId) return;
    await organizationsApi.createInvitation(orgId, {
      email,
      role,
      billing_period: billingPeriod,
      personal_message: message,
    });
    await get().refresh();
  },

  removeMember: async (accountId) => {
    const orgId = get().currentOrg?.id;
    if (!orgId) return;
    await organizationsApi.removeMember(orgId, accountId);
    await get().refresh();
  },

  changeMemberRole: async (accountId, newRole) => {
    const orgId = get().currentOrg?.id;
    if (!orgId) return;
    await organizationsApi.updateOrgMemberRole(orgId, accountId, newRole);
    await get().refresh();
  },

  removeInvitation: (invitationId) => {
    set((state) => ({
      pendingInvitations: state.pendingInvitations.filter((inv) => inv.id !== invitationId),
    }));
  },
}));
